import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import adminAuthService from '../../utils/adminAuthService';
import { useNavigate } from 'react-router-dom';
import logo_up from "../../assets/images/Desktop.jpg";


const API_BASE_URL = process.env.REACT_APP_BACKEND_API;



const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Check if already logged in
  useEffect(() => {
    if (adminAuthService.isAuthenticated()) {
      adminAuthService.validateToken()
        .then(isValid => {
          if (isValid) {
            toast.info('Already logged in, redirecting to dashboard');
            navigate('/admin/dashboard');
          } else {
            // Clear invalid token
            adminAuthService.clearToken();
          }
        });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (!email.trim() || !password.trim()) {
        throw new Error('Email and password are required');
      }

      // Clear any existing tokens before login
      adminAuthService.clearToken();
      
      console.log('Sending login request:', { email: email.trim() });
      const response = await fetch(`${API_BASE_URL}/api/admin/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ 
          email: email.trim(), 
          password: password.trim() 
        }),
        credentials: 'include', // Include cookies if any
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      console.log('Login response received');

      if (!data.token || !data.user_id) {
        throw new Error('Invalid login response');
      }

      // Store the token and user ID
      localStorage.setItem('admin_token', data.token);
      localStorage.setItem('admin_user_id', data.user_id);

      // Give browser time to save to localStorage
      setTimeout(async () => {
        try {
          const isValid = await adminAuthService.validateToken();
          if (!isValid) {
            throw new Error('Token validation failed');
          }
          
          toast.success('Login successful!');
          navigate('/admin/dashboard');
        } catch (err) {
          console.error('Token validation error:', err.message);
          setError('Authentication failed. Please try again.');
          toast.error('Authentication failed. Please try again.');
          setIsLoading(false);
          adminAuthService.clearToken();
        }
      }, 500);
      
    } catch (err) {
      console.error('Login error:', err.message);
      setError(err.message || 'Login failed');
      toast.error(err.message || 'Login failed');
      setIsLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl w-full bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
        {/* Left side - Logo */}
        <div className="w-full md:w-1/2 bg-gradient-to-br p-8 flex items-center justify-center">
          <div className="text-center">
            <img 
              src={logo_up} 
              alt="Company Logo" 
              className="mx-auto w-4/5 max-w-xs h-auto object-contain"
            />
            <p className="mt-4 text-lg  max-w-md">
              Secure access to your admin dashboard and manage your platform with complete control.
            </p>
          </div>
        </div>
        
        {/* Right side - Login Form */}
        <div className="w-full md:w-1/2 p-8 md:p-12 flex items-center justify-center">
          <div className="w-full max-w-md space-y-8">
            <div>
              <h1 className="text-3xl font-semibold text-gray-900">Merchant Portal</h1>
              <p className="mt-2 text-sm text-gray-600">
                Sign in to access your Merchant dashboard
              </p>
            </div>
            
            {error && (
              <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900 sm:text-sm"
                    placeholder="admin@example.com"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    type="password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-3 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900 sm:text-sm"
                    placeholder="Enter your password"
                    required
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                    Forgot password?
                  </a>
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`group relative w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 font-medium ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : 'Sign In'}
                </button>
              </div>
            </form>
            
            <div className="mt-6">
              <p className="text-center text-sm text-gray-600">
                Need technical support?{' '}
                <a href="#" className="font-medium text-blue-600 hover:text-blue-500">
                  Contact IT department
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;